import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minimize2, Plus, Search, Trash2 } from 'lucide-react';
import { useChat } from '../contexts/ChatContext';
import Composer, { ContextChips } from '../components/Assistant/Composer';
import Thread from '../components/Assistant/Thread';
import { EmptyState } from '../components/Assistant/AssistantPanel';
import { hrefFor } from '../components/Assistant/Markdown';

// Full-page assistant: saved chats on the left, the conversation in the
// middle, and every record it cited plus the model on the right.
const mono = 'font-mono text-[10.5px] tracking-[0.12em]';
const KIND = { finding: 'FINDING', ioc: 'IOC', cve: 'CVE', asset: 'ASSET', case: 'CASE', run: 'RUN', action: 'ACTION', misp: 'INTEL' };
const toDate = (iso) => new Date(String(iso).includes('T') ? iso : `${String(iso).replace(' ', 'T')}Z`);

const groupChats = (list) => {
  const now = new Date();
  const today = now.toDateString();
  const weekAgo = now.getTime() - 7 * 86400000;
  const g = { TODAY: [], 'THIS WEEK': [], EARLIER: [] };
  list.forEach(c => {
    const d = toDate(c.updated_at);
    if (d.toDateString() === today) g.TODAY.push(c);
    else if (d.getTime() >= weekAgo) g['THIS WEEK'].push(c);
    else g.EARLIER.push(c);
  });
  return Object.entries(g).filter(([, v]) => v.length);
};

const ROLE_SCOPE = {
  admin: 'read everything in the organisation, and propose links, notes, assignments and playbook runs for you to confirm',
};

export default function Advisor() {
  const navigate = useNavigate();
  const { conversations, conversationId, conversation, loadConversation, deleteConversation, newChat, messages, live, config, setOpen, send } = useChat();
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const groups = groupChats(conversations.filter(c => !needle || c.title.toLowerCase().includes(needle)));
  const empty = !messages.length && !live;

  const sources = useMemo(() => {
    const seen = new Map();
    messages.forEach(m => (m.meta?.citations || []).forEach(c => { const k = `${c.kind}:${c.id}`; if (!seen.has(k)) seen.set(k, c); }));
    return [...seen.values()];
  }, [messages]);

  const dock = () => { setOpen(true); if (window.history.length > 1) navigate(-1); else navigate('/'); };
  const role = (config?.role || '').toLowerCase();

  return (
    <div className="h-[calc(100vh-64px)] min-h-[560px] grid grid-cols-1 lg:grid-cols-[272px_minmax(0,1fr)] xl:grid-cols-[272px_minmax(0,1fr)_320px]" style={{ color: 'var(--ink-text)' }}>
      {/* Chats */}
      <section aria-label="Chats" className="hidden lg:flex min-h-0 flex-col gap-3.5 px-3.5 py-5 border-r overflow-y-auto" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
        <div className="flex items-center justify-between px-1">
          <span className="font-heading text-lg font-semibold">Chats</span>
          <button type="button" onClick={newChat} className="h-[34px] flex items-center gap-1.5 px-2.5 rounded-lg text-[13px] font-semibold" style={{ background: 'var(--accent)', color: '#0A0A0A' }}><Plus className="w-[13px] h-[13px]" strokeWidth={2.2} />New chat</button>
        </div>
        <div className="h-9 box-border flex items-center gap-2 px-2.5 rounded-lg border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
          <Search className="w-3.5 h-3.5 shrink-0" strokeWidth={1.8} style={{ color: 'var(--ink-faint)' }} />
          <input type="search" aria-label="Search chats" placeholder="Search chats" value={q} onChange={e => setQ(e.target.value)} className="flex-grow min-w-0 h-[34px] bg-transparent border-0 outline-none text-[13px]" style={{ color: 'var(--ink-text)' }} />
        </div>
        {!conversations.length && <span className="px-1.5 text-[13px]" style={{ color: 'var(--ink-faint)' }}>Your saved chats appear here.</span>}
        {groups.map(([k, items]) => (
          <div key={k} className="flex flex-col gap-0.5">
            <span className={`${mono} px-1.5 pt-1 pb-1.5`} style={{ color: 'var(--ink-faint)' }}>{k}</span>
            {items.map(c => {
              const on = c.id === conversationId;
              return (
                <div key={c.id} className="group relative flex items-center rounded-lg" style={{ background: on ? 'var(--surf-raised)' : 'transparent' }}>
                  <button type="button" onClick={() => loadConversation(c.id)} aria-pressed={on} className="flex-grow min-w-0 min-h-[50px] box-border flex flex-col justify-center gap-[3px] px-2.5 py-1.5 text-left">
                    <span className={`text-[13px] truncate ${on ? 'font-semibold' : ''}`}>{c.title}</span>
                    <span className="font-mono text-[10.5px] truncate" style={{ color: 'var(--ink-faint)' }}>{toDate(c.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}{c.case_title ? ` · ${c.case_title}` : ''}</span>
                  </button>
                  <button type="button" onClick={() => deleteConversation(c.id)} aria-label={`Delete ${c.title}`} className="w-8 h-8 mr-1 shrink-0 flex items-center justify-center rounded-md opacity-0 group-hover:opacity-100 focus:opacity-100" style={{ color: 'var(--ink-faint)' }}><Trash2 className="w-3.5 h-3.5" /></button>
                  {on && <span aria-hidden="true" className="absolute -right-3.5 top-[13px] w-px h-6" style={{ background: 'var(--accent)' }} />}
                </div>
              );
            })}
          </div>
        ))}
      </section>

      {/* Conversation */}
      <section aria-label="Conversation" className="min-h-0 flex flex-col">
        <div className="h-[60px] shrink-0 box-border flex items-center gap-2.5 px-7 border-b" style={{ borderColor: 'var(--surf-line)' }}>
          <span className="flex-grow min-w-0 flex flex-col gap-[3px]">
            <span className="text-[15px] font-semibold truncate">{conversation?.title || (empty ? 'New chat' : messages.find(m => m.role === 'user')?.content || 'Chat')}</span>
            <span className="font-mono text-[10.5px] truncate" style={{ color: 'var(--ink-faint)' }}>
              {conversation?.case_id ? <>Pinned to <Link to={`/cases/${conversation.case_id}`} className="hover:underline" style={{ color: 'var(--ink-link)' }}>{conversation.case_title || conversation.case_id}</Link> · only you can see this chat</> : 'Only you can see this chat'}
            </span>
          </span>
          <button type="button" onClick={dock} aria-label="Dock beside the page" className="w-9 h-9 flex items-center justify-center rounded-lg border" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}><Minimize2 className="w-[15px] h-[15px]" strokeWidth={1.9} /></button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto flex justify-center px-7 py-[22px]">
          <div className="w-full max-w-[680px]">
            {empty ? <EmptyState onPick={(t) => send(t)} /> : <Thread wide />}
          </div>
        </div>
        <div className="shrink-0 flex justify-center px-7 pt-3 pb-[18px] border-t" style={{ borderColor: 'var(--surf-line)' }}>
          <div className="w-full max-w-[680px] flex flex-col gap-2">
            <ContextChips compact />
            <Composer wide placeholder={empty ? 'Ask about findings, alerts, assets or cases. Type / for commands' : 'Ask a follow-up, or type / for commands'} />
          </div>
        </div>
      </section>

      {/* Sources and settings */}
      <aside aria-label="Sources and settings" className="hidden xl:flex min-h-0 flex-col gap-[22px] p-5 border-l overflow-y-auto" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
        <div className="flex flex-col gap-2">
          <span className={mono} style={{ color: 'var(--ink-faint)' }}>SOURCES · {sources.length} RECORD{sources.length === 1 ? '' : 'S'}</span>
          {!sources.length && <span className="text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>Records the assistant reads and cites are listed here, with links.</span>}
          {sources.map(s => {
            const href = hrefFor(s);
            const inner = (
              <>
                <span className="flex gap-2 items-baseline min-w-0">
                  <span className="font-mono text-[9.5px] tracking-[0.08em] w-[58px] shrink-0" style={{ color: 'var(--ink-faint)' }}>{KIND[s.kind]}</span>
                  <span className="font-mono text-[11.5px] truncate" style={{ color: href ? 'var(--ink-link)' : 'var(--ink-text)' }}>{s.kind === 'run' ? `RUN-${s.id.slice(0, 8).toUpperCase()}` : s.kind === 'ioc' || s.kind === 'cve' ? `#${s.id}` : s.kind === 'asset' ? s.label : s.id}</span>
                </span>
                <span className="text-xs pl-[66px] truncate" style={{ color: 'var(--ink-muted)' }}>{s.kind === 'asset' ? s.meta : s.label}</span>
              </>
            );
            const cls = 'flex flex-col gap-[3px] px-2.5 py-2 rounded-[9px] border min-w-0';
            const style = { background: 'var(--surf-bg)', borderColor: 'var(--surf-line)', color: 'var(--ink-text)' };
            return href ? <Link key={`${s.kind}:${s.id}`} to={href} className={cls} style={style}>{inner}</Link> : <div key={`${s.kind}:${s.id}`} className={cls} style={style}>{inner}</div>;
          })}
        </div>
        <div className="flex flex-col gap-2">
          <span className={mono} style={{ color: 'var(--ink-faint)' }}>MODEL</span>
          {config?.configured ? (
            <div className="flex items-center gap-2.5 px-2.5 py-[9px] rounded-[9px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--accent)' }}>
              <span className="flex-grow min-w-0 flex flex-col gap-0.5">
                <span className="text-[12.5px] font-medium truncate">{config.provider === 'openrouter' ? 'OpenRouter' : config.provider === 'claude' ? 'Claude' : 'Ollama'} · {config.model}</span>
                <span className="text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{config.local ? 'Runs on your own server' : 'Hosted by the provider'}</span>
              </span>
              <span className="h-5 inline-flex items-center gap-[5px] px-1.5 rounded-[5px] font-mono text-[9.5px] tracking-[0.06em] shrink-0" style={{ background: config.local ? 'var(--sev-good-bg)' : 'var(--sev-high-bg)' }}>
                <span className="w-[5px] h-[5px] rounded-full" style={{ background: config.local ? 'var(--sev-good-fg)' : 'var(--sev-high-fg)' }} />{config.local ? 'LOCAL' : 'EXTERNAL'}
              </span>
            </div>
          ) : <span className="text-[12.5px]" style={{ color: 'var(--sev-crit-fg)' }}>No model configured.</span>}
          <span className="text-[11.5px] leading-normal" style={{ color: 'var(--ink-faint)' }}>Set by an admin in Settings › Integrations (Ollama first, then Claude, then OpenRouter). {config?.local ? 'Records stay on your network.' : 'External models receive the records listed above.'}</span>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className={mono} style={{ color: 'var(--ink-faint)' }}>WHAT IT MAY DO FOR YOU</span>
          <span className="text-[12.5px] leading-relaxed" style={{ color: 'var(--ink-muted)' }}>
            {config?.role ? `As ${/^[aeiou]/i.test(config.role) ? 'an' : 'a'} ${config.role}: ` : ''}{ROLE_SCOPE[role] || 'read the records in your organisation, and propose links, notes, assignments and playbook runs for you to confirm'}. It never approves playbook gates or confirms response actions, and every change goes through your own permissions.
          </span>
        </div>
      </aside>
    </div>
  );
}
